import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Car, Users, Plus, Trash2, QrCode, MapPin, Loader2, Clock, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { downloadQrCard } from "../lib/qrCardGenerator";
import { SaudiPlate } from "../components/SaudiPlate";
import { brandLogo, colorHex } from "../components/carBrands";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ar-SA", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function ValetAdmin() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [drivers, setDrivers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [cars, setCars] = useState([]);
  const [settings, setSettings] = useState({ enabled: false, lat: "", lng: "", radius_m: 100, weekly_off_days: [5] });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [newDriver, setNewDriver] = useState({ name: "", code: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState(null);
  const [brands, setBrands] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [calDriver, setCalDriver] = useState("all");
  const [monthShifts, setMonthShifts] = useState([]);
  const [carSearch, setCarSearch] = useState("");

  useEffect(() => {
    const s = carSearch.trim();
    if (!s) return;
    const id = setTimeout(() => {
      axios.get(`${API}/valet/cars?q=${encodeURIComponent(s)}`, { headers })
        .then((r) => setCars(r.data)).catch(() => {});
    }, 350);
    return () => clearTimeout(id);
  }, [carSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!carSearch.trim()) {
      axios.get(`${API}/valet/cars?date=${date}`, { headers })
        .then((r) => setCars(r.data)).catch(() => {});
    }
  }, [carSearch, date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${API}/valet/shifts?month=${month}`, { headers })
      .then((r) => setMonthShifts(r.data))
      .catch(() => {});
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBrands = async () => {
    try { setBrands((await axios.get(`${API}/valet/public/brands`)).data); } catch {}
  };
  useEffect(() => { loadBrands(); }, []);

  const moveBrand = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= brands.length) return;
    const next = [...brands];
    [next[idx], next[to]] = [next[to], next[idx]];
    setBrands(next);
    try {
      await axios.put(`${API}/valet/brands/order`, { ids: next.map((b) => b.id) }, { headers });
    } catch { toast.error("فشل حفظ الترتيب"); loadBrands(); }
  };

  const load = useCallback(async () => {
    try {
      const [d, sh, c, st] = await Promise.all([
        axios.get(`${API}/valet/drivers`, { headers }),
        axios.get(`${API}/valet/shifts?date=${date}`, { headers }),
        axios.get(`${API}/valet/cars?date=${date}`, { headers }),
        axios.get(`${API}/valet/settings`, { headers }),
      ]);
      setDrivers(d.data); setShifts(sh.data); setCars(c.data);
      setSettings({ enabled: !!st.data.enabled, lat: st.data.lat ?? "", lng: st.data.lng ?? "", radius_m: st.data.radius_m ?? 100, weekly_off_days: st.data.weekly_off_days ?? [5] });
    } catch { toast.error("فشل تحميل البيانات"); }
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const addDriver = async () => {
    if (!newDriver.name.trim()) { toast.error("اسم السائق مطلوب"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/valet/drivers`, newDriver, { headers });
      toast.success(`تمت الإضافة — رمز الدخول: ${res.data.code}`);
      setAddOpen(false); setNewDriver({ name: "", code: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الإضافة"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    try {
      await axios.put(`${API}/valet/drivers/${d.id}`, { active: !d.active }, { headers });
      load();
    } catch { toast.error("فشل التحديث"); }
  };

  const removeDriver = async (d) => {
    if (!window.confirm(`حذف السائق ${d.name}؟`)) return;
    try {
      await axios.delete(`${API}/valet/drivers/${d.id}`, { headers });
      toast.success("تم الحذف"); load();
    } catch { toast.error("فشل الحذف"); }
  };

  const saveSettings = async () => {
    if (settings.enabled && (settings.lat === "" || settings.lng === "")) { toast.error("أدخل الإحداثيات أولاً"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/valet/settings`, {
        enabled: settings.enabled,
        lat: parseFloat(settings.lat || 0),
        lng: parseFloat(settings.lng || 0),
        radius_m: parseInt(settings.radius_m || 100),
        weekly_off_days: settings.weekly_off_days,
      }, { headers });
      toast.success("تم حفظ إعدادات النطاق الجغرافي");
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحفظ"); }
    finally { setSaving(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("المتصفح لا يدعم تحديد الموقع"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSettings((s) => ({ ...s, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }));
        toast.success("تم التقاط موقعك الحالي");
      },
      () => toast.error("تعذر الحصول على موقعك"),
      { enableHighAccuracy: true }
    );
  };

  const showQr = async () => {
    try {
      const res = await axios.get(`${API}/valet/qr-code?base_url=${encodeURIComponent(window.location.origin)}`, { headers });
      setQr(res.data);
    } catch { toast.error("فشل توليد رمز QR"); }
  };

  const openShifts = shifts.filter((s) => !s.checkout_at);

  return (
    <div dir="rtl" className="space-y-6" data-testid="valet-admin-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2"><Car className="w-6 h-6 text-[#655dc6]" /> Valet Parking</h1>
          <p className="text-slate-500 text-sm mt-1">إدارة السائقين، سجل الحضور وسجل السيارات</p>
        </div>
        <Button onClick={showQr} variant="outline" className="gap-1.5" data-testid="valet-qr-btn"><QrCode className="w-4 h-4" /> رمز QR</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "السائقون", value: drivers.length, icon: Users },
          { label: "حاضرون الآن", value: openShifts.length, icon: Clock },
          { label: `سيارات ${date}`, value: cars.length, icon: Car },
          { label: `ورديات ${date}`, value: shifts.length, icon: Clock },
        ].map((k) => (
          <Card key={k.label} className="border-0 shadow-sm"><CardContent className="py-4 px-5">
            <p className="text-2xl font-bold text-[#4a3fa8]">{k.value}</p>
            <p className="text-xs text-slate-500">{k.label}</p>
          </CardContent></Card>
        ))}
      </div>

      <Tabs defaultValue="drivers" dir="rtl">
        <TabsList>
          <TabsTrigger value="drivers" data-testid="tab-drivers">السائقون</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">الحضور والانصراف</TabsTrigger>
          <TabsTrigger value="cars" data-testid="tab-cars">السيارات</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">الإعدادات</TabsTrigger>
        </TabsList>

        <TabsContent value="drivers" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => setAddOpen(true)} className="bg-[#655dc6] hover:bg-[#5349b8] text-white gap-1.5" data-testid="add-driver-btn">
              <Plus className="w-4 h-4" /> إضافة سائق
            </Button>
          </div>
          <Card className="border-0 shadow-sm"><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-right text-slate-500">
                <th className="py-3 px-4">الاسم</th><th className="py-3 px-4">رمز الدخول</th><th className="py-3 px-4">الحالة</th><th className="py-3 px-4"></th>
              </tr></thead>
              <tbody>
                {drivers.map((d) => (
                  <tr key={d.id} className="border-b last:border-0" data-testid={`driver-row-${d.id}`}>
                    <td className="py-3 px-4 font-semibold">{d.name}</td>
                    <td className="py-3 px-4 font-mono font-bold text-[#4a3fa8]" dir="ltr">{d.code}</td>
                    <td className="py-3 px-4">
                      <button onClick={() => toggleActive(d)} data-testid={`driver-toggle-${d.id}`}>
                        <Badge className={d.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>{d.active ? "نشط" : "موقوف"}</Badge>
                      </button>
                    </td>
                    <td className="py-3 px-4 text-left">
                      <Button variant="ghost" size="sm" onClick={() => removeDriver(d)} className="text-red-500 hover:text-red-600" data-testid={`driver-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
                {drivers.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-400">لا يوجد سائقون بعد</td></tr>}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="attendance" className="space-y-3">
          {(() => {
            const [y, m] = month.split("-").map(Number);
            const daysInMonth = new Date(y, m, 0).getDate();
            const firstDow = new Date(y, m - 1, 1).getDay();
            const todayStr = new Date().toLocaleDateString("en-CA");
            const fmtT = (iso) => new Date(iso).toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" });
            const visible = monthShifts.filter((s) => calDriver === "all" || s.driver_id === calDriver);
            const byDay = {};
            visible.forEach((s) => {
              const key = new Date(s.checkin_at).toLocaleDateString("en-CA");
              (byDay[key] = byDay[key] || []).push(s);
            });
            let present = 0, absent = 0;
            const offDays = settings.weekly_off_days || [];
            const isOffDay = (d) => offDays.includes(new Date(y, m - 1, d).getDay());
            if (calDriver !== "all") {
              for (let d = 1; d <= daysInMonth; d++) {
                const ds = `${month}-${String(d).padStart(2, "0")}`;
                if (ds > todayStr) continue;
                if (byDay[ds]?.length) present++;
                else if (!isOffDay(d)) absent++;
              }
            }
            return (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    {calDriver !== "all" && (
                      <div className="flex gap-2 text-xs">
                        <Badge className="bg-emerald-100 text-emerald-700" data-testid="present-count">حضور: {present} يوم</Badge>
                        <Badge className="bg-red-100 text-red-700" data-testid="absent-count">غياب: {absent} يوم</Badge>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={calDriver} onChange={(e) => setCalDriver(e.target.value)}
                      className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="calendar-driver-filter">
                      <option value="all">كل السائقين</option>
                      {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="calendar-month-filter" />
                  </div>
                </div>
                <Card className="border-0 shadow-sm"><CardContent className="p-3 sm:p-4">
                  <div className="grid grid-cols-7 gap-1 mb-1 text-center text-[11px] font-bold text-slate-500">
                    {["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"].map((d) => <div key={d} className="py-1">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1" data-testid="attendance-calendar">
                    {Array.from({ length: firstDow }).map((_, i) => <div key={`b${i}`} className="min-h-[6.75rem] rounded-lg bg-slate-50/50" />)}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const d = i + 1;
                      const ds = `${month}-${String(d).padStart(2, "0")}`;
                      const dayShifts = byDay[ds] || [];
                      const isToday = ds === todayStr;
                      const isFuture = ds > todayStr;
                      const off = isOffDay(d);
                      const isAbsent = calDriver !== "all" && !isFuture && !off && dayShifts.length === 0;
                      return (
                        <div key={ds}
                          className={`min-h-[6.75rem] rounded-lg border p-1.5 text-right overflow-hidden ${isToday ? "border-[#655dc6] ring-1 ring-[#655dc6]/30" : "border-slate-100"} ${isAbsent ? "bg-red-50" : dayShifts.length ? "bg-emerald-50/40" : off ? "bg-slate-100/70" : "bg-white"}`}
                          data-testid={`cal-day-${ds}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-[11px] font-bold ${isToday ? "text-[#655dc6]" : "text-slate-500"}`}>{d}</span>
                            {isAbsent && <span className="text-[9px] font-bold text-red-500">غائب</span>}
                            {off && !dayShifts.length && <span className="text-[9px] font-bold text-slate-400">عطلة</span>}
                          </div>
                          <div className="space-y-0.5">
                            {dayShifts.map((s) => (
                              <div key={s.id} className="bg-white border border-slate-100 rounded px-1.5 py-1 leading-tight" title={s.driver_name}>
                                {calDriver === "all" && <span className="block text-[10px] font-bold text-slate-600 truncate text-right" dir="rtl">{s.driver_name}</span>}
                                <span className="block text-[11px] font-semibold text-emerald-600 text-left" dir="ltr">↓ {fmtT(s.checkin_at)}</span>
                                <span className={`block text-[11px] font-semibold text-left ${s.checkout_at ? "text-red-500" : "text-amber-500"}`} dir="ltr">
                                  {s.checkout_at ? `↑ ${fmtT(s.checkout_at)}` : "● حاضر الآن"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent></Card>
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="cars" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="relative">
              <Input value={carSearch} onChange={(e) => setCarSearch(e.target.value)}
                placeholder="بحث بأحرف أو أرقام اللوحة…" dir="ltr"
                className="w-64 pl-3 text-left font-mono" data-testid="car-plate-search" />
            </div>
            <div className="flex items-center gap-2">
              {carSearch.trim() && <Badge className="bg-[#655dc6]/10 text-[#655dc6]" data-testid="car-search-results-count">نتائج البحث: {cars.length} — كل التواريخ</Badge>}
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={!!carSearch.trim()} className="w-44" data-testid="car-date-filter" />
            </div>
          </div>
          <Card className="border-0 shadow-sm"><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-right text-slate-500">
                <th className="py-3 px-4">النوع</th><th className="py-3 px-4">اللون</th><th className="py-3 px-4">اللوحة</th><th className="py-3 px-4">السائق</th><th className="py-3 px-4">الوقت</th>
              </tr></thead>
              <tbody>
                {cars.map((c) => (
                  <tr key={c.id} className="border-b last:border-0" data-testid={`admin-car-row-${c.id}`}>
                    <td className="py-3 px-4 font-semibold">
                      <span className="inline-flex items-center gap-2">
                        {brandLogo(c.car_type) && <img src={brandLogo(c.car_type)} alt="" className="h-5 w-9 object-contain" />}
                        {c.car_type}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {c.color ? (
                        <span className="inline-flex items-center gap-1.5">
                          {colorHex(c.color) && <span className="w-4 h-4 rounded-md border border-slate-300 inline-block" style={{ backgroundColor: colorHex(c.color) }} />}
                          {c.color}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="py-3 px-4 text-base"><SaudiPlate plate={c.plate} /></td>
                    <td className="py-3 px-4">{c.driver_name}</td>
                    <td className="py-3 px-4 text-xs">{fmt(c.logged_at)}</td>
                  </tr>
                ))}
                {cars.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">لا توجد سيارات مسجلة لهذا اليوم</td></tr>}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="settings">
          <div className="grid lg:grid-cols-2 gap-4 items-stretch">
            <Card className="border-0 shadow-sm">
              <CardContent className="py-6 px-6 space-y-5">
                <div className="flex items-start gap-3 pb-4 border-b">
                  <div className="w-10 h-10 rounded-xl bg-[#655dc6]/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[#655dc6]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A0A0A]">النطاق الجغرافي (Geofence)</h3>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">عند التفعيل، لا يمكن للسائق تسجيل الحضور أو الانصراف إلا داخل النطاق المحدد حول المركز.</p>
                  </div>
                </div>
                <div className={`flex items-center justify-between rounded-xl px-4 py-3.5 border transition-colors ${settings.enabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                  <div>
                    <span className="text-sm font-semibold block">تفعيل التحقق من الموقع</span>
                    <span className={`text-[11px] ${settings.enabled ? "text-emerald-600" : "text-slate-400"}`}>{settings.enabled ? "مفعّل — التسجيل من داخل النطاق فقط" : "معطّل — التسجيل متاح من أي مكان"}</span>
                  </div>
                  <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} data-testid="geofence-toggle" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-600">خط العرض (Latitude)</label>
                    <Input dir="ltr" value={settings.lat} onChange={(e) => setSettings({ ...settings, lat: e.target.value })} placeholder="24.7136" data-testid="geofence-lat" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-600">خط الطول (Longitude)</label>
                    <Input dir="ltr" value={settings.lng} onChange={(e) => setSettings({ ...settings, lng: e.target.value })} placeholder="46.6753" data-testid="geofence-lng" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">نصف القطر المسموح (بالأمتار)</label>
                  <Input dir="ltr" type="number" value={settings.radius_m} onChange={(e) => setSettings({ ...settings, radius_m: e.target.value })} data-testid="geofence-radius" />
                </div>
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <Button variant="outline" onClick={useMyLocation} className="gap-1.5" data-testid="use-my-location-btn"><MapPin className="w-4 h-4" /> استخدام موقعي الحالي</Button>
                  <Button onClick={saveSettings} disabled={saving} className="bg-[#655dc6] hover:bg-[#5349b8] text-white flex-1" data-testid="save-settings-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ الإعدادات"}
                  </Button>
                </div>
                <div className="pt-4 border-t space-y-2">
                  <h4 className="text-sm font-bold">أيام العطلة الأسبوعية</h4>
                  <p className="text-[11px] text-slate-500">الأيام المحددة لا تُحسب غياباً على السائق في تقويم الحضور. يُحفظ مع زر "حفظ الإعدادات".</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="off-days-picker">
                    {["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"].map((label, dow) => {
                      const on = (settings.weekly_off_days || []).includes(dow);
                      return (
                        <button key={dow} type="button"
                          onClick={() => setSettings((s) => ({ ...s, weekly_off_days: on ? s.weekly_off_days.filter((x) => x !== dow) : [...(s.weekly_off_days || []), dow] }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-[#655dc6] text-white border-[#655dc6]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                          data-testid={`off-day-${dow}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="lg:relative lg:h-full">
              <Card className="border-0 shadow-sm flex flex-col max-h-[30rem] lg:max-h-none lg:absolute lg:inset-0">
                <CardContent className="py-6 px-6 space-y-4 flex flex-col flex-1 min-h-0">
                <div className="flex items-start gap-3 pb-4 border-b">
                  <div className="w-10 h-10 rounded-xl bg-[#655dc6]/10 flex items-center justify-center shrink-0">
                    <Car className="w-5 h-5 text-[#655dc6]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A0A0A]">ترتيب أنواع السيارات</h3>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">الأعلى في القائمة يظهر أولاً للسائق — ضع الأكثر استخداماً بالأعلى. الترتيب يُحفظ تلقائياً.</p>
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto space-y-1 pl-1" data-testid="brand-order-list">
                  {brands.map((b, i) => (
                    <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white hover:bg-slate-50 px-3 py-2 transition-colors" data-testid={`brand-order-${b.file}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] font-bold text-slate-400 bg-slate-100 rounded-md w-6 h-6 flex items-center justify-center">{i + 1}</span>
                        <img src={`/car-logos/${b.file}.png`} alt="" className="h-6 w-10 object-contain" loading="lazy" />
                        <span className="text-sm font-semibold">{b.name}</span>
                      </div>
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="sm" disabled={i === 0} onClick={() => moveBrand(i, -1)} className="h-7 w-7 p-0 text-slate-500 hover:text-[#655dc6]" data-testid={`brand-up-${b.file}`}><ChevronUp className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" disabled={i === brands.length - 1} onClick={() => moveBrand(i, 1)} className="h-7 w-7 p-0 text-slate-500 hover:text-[#655dc6]" data-testid={`brand-down-${b.file}`}><ChevronDown className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader><DialogTitle>رمز QR — Valet Parking</DialogTitle></DialogHeader>
          {qr && (
            <div className="space-y-3">
              <img src={`data:image/png;base64,${qr.qr_code}`} alt="Valet QR" className="mx-auto w-56 h-56" data-testid="valet-qr-img" />
              <p className="text-xs text-slate-400 break-all" dir="ltr">{qr.url}</p>
              <Button
                onClick={() => downloadQrCard({ qrBase64: qr.qr_code, name: "Valet Parking", floor: "", building: "", bottomTextAr: "امسح الرمز لتسجيل حضور وسيارات الـ Valet", bottomTextEn: "Scan for valet check-in & car logging" })}
                className="w-full bg-[#655dc6] hover:bg-[#5349b8] text-white" data-testid="download-valet-qr-btn">
                تحميل بطاقة QR
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>إضافة سائق</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="اسم السائق *" value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} data-testid="new-driver-name" />
            <Input placeholder="رمز الدخول 4-8 أرقام (اتركه فارغاً للتوليد التلقائي)" dir="ltr" value={newDriver.code}
              onChange={(e) => setNewDriver({ ...newDriver, code: e.target.value.replace(/\D/g, "").slice(0, 8) })} data-testid="new-driver-code" />
            <Button onClick={addDriver} disabled={saving} className="w-full bg-[#655dc6] hover:bg-[#5349b8] text-white" data-testid="new-driver-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "إضافة"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
